import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService, { AuthenticationService } from '../../services/authService';

// authService.fetchCompleteUserProfile bazı tarih alanlarını Date objesi
// olarak döndürüyordu (ör. birthDate). Date Redux serializableCheck'ten
// geçmiyor — console'u "non-serializable" warning ile dolduruyordu. Store'a
// koymadan önce Date alanlarını ISO string'e çeviriyoruz; tüketici tarafta
// yine `new Date(...)` ile parse edilebilir.
const serializeUser = (user) => {
  if (!user || typeof user !== 'object') return user;
  const normalized = { ...user };
  for (const [key, value] of Object.entries(normalized)) {
    if (value instanceof Date) {
      normalized[key] = Number.isNaN(value.getTime()) ? null : value.toISOString();
    }
  }
  return normalized;
};

/**
 * @typedef {Object} AuthState
 * @property {Object|null} user - Authenticated user profile
 * @property {string|null} accessToken - Current access token
 * @property {string|null} refreshToken - Current refresh token
 * @property {boolean} isAuthenticated - Authentication status
 * @property {boolean} isLoading - Loading state for auth operations
 * @property {boolean} isRefreshing - Token refresh in progress
 * @property {string|null} error - Current error message
 * @property {boolean} initialized - Auth state initialization status
 */

/**
 * Initial authentication state
 * @type {AuthState}
 */
const initialAuthState = Object.freeze({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  isRefreshing: false,
  error: null,
  initialized: false,
});

/**
 * Initialize authentication state from storage
 * Runs on app startup to restore previous session
 */
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const accessToken = authService.getAccessToken();
      const refreshToken = authService.getRefreshToken();
      const storedUserProfile = authService.getUserProfile();

      let isAuthenticated = authService.isAuthenticated();

      // Sunucu-tarafi session validation. 401 iki farkli sey demek olabilir:
      //   (a) Access token expire olmus — refresh token ile yenile
      //   (b) Kullanici pasif (isActive=false / work_end_date gecti) —
      //       gercekten clear + login
      // Once refresh dene: kullanici bilgisayari 3 gun kapali tutup acinca
      // access token kesin expire, refresh token 7 gun (localStorage'da). Bu
      // durumda direkt clearAuth yapmak refresh token'i da siliyor ve
      // "kapatinca login'e atiyor" bug'i olusuyor.
      if (isAuthenticated) {
        let check = await authService.validateSession();
        if (!check.ok && check.status === 401) {
          const refreshed = await authService.refreshTokens().catch(() => null);
          if (refreshed?.success) {
            check = await authService.validateSession();
          }
          if (!check.ok && check.status === 401) {
            try { authService._clearAuthData(); } catch (_e) { /* */ }
            isAuthenticated = false;
            return { user: null, accessToken: null, refreshToken: null, isAuthenticated: false };
          }
        }
      }

      // If we have a stored user profile, fetch the complete profile with team and group info
      let userProfile = storedUserProfile;
      if (storedUserProfile?.id && isAuthenticated) {
        try {
          const completeProfile = await authService.fetchCompleteUserProfile(storedUserProfile.id);
          if (completeProfile) {
            userProfile = completeProfile;
            // Update stored profile with complete information
            authService._storage.setItem(
              AuthenticationService.STORAGE_KEYS.USER_PROFILE,
              JSON.stringify(completeProfile)
            );
          }
        } catch (profileError) {
          console.warn('Failed to fetch complete user profile:', profileError);
          // Continue with stored profile if fetch fails
        }
      }

      return {
        user: userProfile,
        accessToken,
        refreshToken,
        isAuthenticated
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Initialization failed');
    }
  }
);

/**
 * Authenticate user with credentials
 * Handles login process with proper error handling
 */
export const authenticateUser = createAsyncThunk(
  'auth/authenticate',
  async (credentials, { rejectWithValue }) => {
    try {
      const result = await authService.authenticate(credentials);

      if (result.success) {
        return {
          user: result.user,
          accessToken: result.token,
          refreshToken: result.refreshToken
        };
      } else {
        return rejectWithValue(result.error || 'Authentication failed');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Authentication failed');
    }
  }
);

/**
 * Invalidate user session
 * Handles logout process and cleanup
 */
export const invalidateSession = createAsyncThunk(
  'auth/invalidateSession',
  async (_, { rejectWithValue }) => {
    try {
      const result = await authService.invalidateSession();
      
      return {
        message: result.message || 'Session invalidated successfully'
      };
    } catch (error) {
      // Don't reject logout - always clear local state
      return {
        message: 'Session cleared'
      };
    }
  }
);

/**
 * Refresh authentication tokens
 * Handles token refresh with retry logic
 */
export const refreshAuthTokens = createAsyncThunk(
  'auth/refreshTokens',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      
      if (auth.isRefreshing) {
        throw new Error('Token refresh already in progress');
      }

      const result = await authService.refreshTokens();

      if (result.success) {
        return {
          accessToken: result.token,
          refreshToken: result.refreshToken
        };
      } else {
        return rejectWithValue(result.error || 'Token refresh failed');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Token refresh failed');
    }
  }
);

/**
 * Authentication slice with comprehensive state management
 * Follows Redux Toolkit patterns and best practices
 */
const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    /**
     * Clear authentication error
     */
    clearAuthError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
        state.initialized = false;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        const { user, accessToken, refreshToken, isAuthenticated } = action.payload;

        state.isLoading = false;
        state.initialized = true;
        state.user = serializeUser(user);
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        state.isAuthenticated = isAuthenticated;
        state.error = null;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = action.payload;
      })

      // Authentication Cases
      .addCase(authenticateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(authenticateUser.fulfilled, (state, action) => {
        const { user, accessToken, refreshToken } = action.payload;

        state.isLoading = false;
        state.user = serializeUser(user);
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(authenticateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
      })

      .addCase(invalidateSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(invalidateSession.fulfilled, (state) => {
        return {
          ...initialAuthState,
          initialized: true // Keep initialized flag
        };
      })
      .addCase(invalidateSession.rejected, (state) => {
        return {
          ...initialAuthState,
          initialized: true
        };
      })

      .addCase(refreshAuthTokens.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(refreshAuthTokens.fulfilled, (state, action) => {
        const { accessToken, refreshToken } = action.payload;
        
        state.isRefreshing = false;
        state.accessToken = accessToken;
        
        if (refreshToken) {
          state.refreshToken = refreshToken;
        }
        
        state.error = null;
      })
      .addCase(refreshAuthTokens.rejected, (state, action) => {
        state.isRefreshing = false;
        state.error = action.payload;
        
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
      });
  },
});

// Action creators
export const { clearAuthError } = authSlice.actions;

// Memoized selectors for optimal performance
export const selectAuthState = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectIsRefreshing = (state) => state.auth.isRefreshing;
export const selectAuthError = (state) => state.auth.error;
export const selectIsInitialized = (state) => state.auth.initialized;
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectRefreshToken = (state) => state.auth.refreshToken;

// Computed selectors
export const selectUserDisplayName = (state) => {
  const user = selectUser(state);
  if (!user) return '';
  return user.name || `${user.firstName} ${user.lastName}`.trim() || user.email || 'User';
};

export const selectHasRefreshToken = (state) => {
  return !!selectRefreshToken(state);
};

export const selectIsOperationInProgress = (state) => {
  return selectIsLoading(state) || selectIsRefreshing(state);
};

// Export reducer as default
export default authSlice.reducer;
